import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CourierFilesApiAdapter } from '../adapters';
import { CourierFileFilters, CourierFilesResponse } from '../models';

interface UseCourierFilesProps {
  filters: Omit<CourierFileFilters, 'page'>;
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  enabled?: boolean;
}

type Meta = CourierFilesResponse['meta'] | Record<string, unknown> | undefined;

const getTotalFromMeta = (meta: Meta): number => {
  if (meta == null) return 0;
  const m = meta as Record<string, unknown>;
  const t = m.total;
  if (typeof t === 'number') return t;
  if (typeof t === 'string') return parseInt(t, 10) || 0;
  return 0;
};

const getNextPageParamFromResponse = (lastPage: CourierFilesResponse | undefined): number | undefined => {
  if (!lastPage?.meta) return undefined;
  const meta = lastPage.meta as Record<string, unknown>;
  const currentPage = (meta.current_page as number | undefined) ?? (meta.currentPage as number | undefined);
  const lastPageNum = (meta.last_page as number | undefined) ?? (meta.lastPage as number | undefined);
  if (currentPage != null && lastPageNum != null && currentPage < lastPageNum) {
    return currentPage + 1;
  }
  return undefined;
};

export const useCourierFiles = ({ filters, sorting, enabled = true }: UseCourierFilesProps) => {
  const httpClient = createHttpClient(apiClientWH);
  const courierFilesAdapter = new CourierFilesApiAdapter(httpClient);

  const { data, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQuery({
      queryKey: ['courier-cod-files', filters, sorting],
      queryFn: ({ pageParam = 1 }) => courierFilesAdapter.getCourierFiles({ ...filters, page: pageParam as number }),
      getNextPageParam: (lastPage) => getNextPageParamFromResponse(lastPage),
      initialPageParam: 1,
      enabled,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      placeholderData: (previousData) => previousData,
    });

  const courierFiles = data?.pages.flatMap((page) => page?.data ?? []) ?? [];
  const firstMeta = data?.pages?.[0]?.meta;
  const total = getTotalFromMeta(firstMeta);

  return {
    courierFiles,
    meta: firstMeta,
    total,
    links: data?.pages?.[0]?.links,
    isLoading: isLoading || isPending,
    isFetching,
    error: error ? (error as Error).message : null,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};
